import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/seo/Seo";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * FAQ items are defined as IDs only — the actual visible strings live in
 * src/i18n/locales/{it,en,...}.ts under keys like:
 *   faq.customer.<id>.title
 *   faq.customer.<id>.intro      (optional preamble)
 *   faq.customer.<id>.items      (one big string with '\n' separated bullets)
 *   faq.customer.<id>.ordered    (literal '1' if it should render as <ol>; otherwise <ul>)
 */
const customerFaq = [
  { id: 'home_flow', ordered: true },
  { id: 'single', ordered: true },
  { id: 'recurring', ordered: true },
  { id: 'quote', ordered: true },
  { id: 'changes', ordered: false },
  { id: 'auth', ordered: false },
  { id: 'address', ordered: false },
  { id: 'notifications', ordered: false },
  { id: 'privacy', ordered: false },
];

const vendorFaq = [
  { id: 'access', ordered: true },
  { id: 'profile_services', ordered: true },
  { id: 'availability', ordered: true },
  { id: 'bookings', ordered: false },
  { id: 'quotes', ordered: true },
  { id: 'payments', ordered: false },
  { id: 'reporting', ordered: false },
  { id: 'offers', ordered: true },
  { id: 'paymethods', ordered: false },
  { id: 'cancel_policy', ordered: false },
  { id: 'messaging', ordered: false },
  { id: 'security', ordered: false },
];

function FaqList({ group, item }: { group: 'customer' | 'vendor'; item: { id: string; ordered: boolean } }) {
  const { t } = useI18n();
  const raw = t(`faq.${group}.${item.id}.items`, '');
  const lines = raw.split('\n').map((l) => l.trim()).filter(Boolean);
  const ListTag = item.ordered ? 'ol' : 'ul';
  const listClass = item.ordered ? 'list-decimal pl-5 space-y-2' : 'list-disc pl-5 space-y-2';
  return (
    <ListTag className={listClass}>
      {lines.map((line, idx) => (
        <li key={idx}>{line}</li>
      ))}
    </ListTag>
  );
}

export default function FAQ() {
  const { t } = useI18n();
  return (
    <div className="container mx-auto px-6 py-10">
      <Seo
        title={t('faq.seo.title', 'FAQ | Halomora')}
        description={t('faq.seo.description', 'Frequently asked questions for customers and businesses: bookings, payments, quotes, recurring services, cancellations and support.')}
        canonicalPath="/faq"
      />
      <div className="max-w-5xl mx-auto space-y-8">
        <header className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('faq.page_title', 'Frequently asked questions (FAQ)')}</h1>
          <p className="text-gray-600 mt-2">{t('faq.page_subtitle', 'Simple instructions for Customers and Vendors')}</p>
        </header>

        <Card className="border-gray-200 shadow-card">
          <CardHeader>
            <CardTitle className="text-xl">{t('faq.customer.section_title', 'Customer FAQs (B2C and B2B)')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {customerFaq.map((q) => (
                <AccordionItem key={q.id} value={q.id}>
                  <AccordionTrigger className="text-left">{t(`faq.customer.${q.id}.title`, q.id)}</AccordionTrigger>
                  <AccordionContent>
                    <FaqList group="customer" item={q} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-card">
          <CardHeader>
            <CardTitle className="text-xl">{t('faq.vendor.section_title', 'Vendor FAQs (Companies / Partners)')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {vendorFaq.map((q) => (
                <AccordionItem key={q.id} value={q.id}>
                  <AccordionTrigger className="text-left">{t(`faq.vendor.${q.id}.title`, q.id)}</AccordionTrigger>
                  <AccordionContent>
                    <FaqList group="vendor" item={q} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
